/**
 * Trade Calculation Utilities
 * Pure functions for calculating trade values and statistics
 */

import { Trade, TradeStatistics } from "../types/trade";

/**
 * Calculate entry value (entryPrice * entryQuantity)
 */
export const calculateEntryValue = (
  entryPrice: number,
  entryQuantity: number
): number => {
  if (entryPrice <= 0 || entryQuantity <= 0) {
    return 0;
  }
  return entryPrice * entryQuantity;
};

/**
 * Calculate exit value (exitPrice * exitQuantity or entryQuantity if not specified)
 */
export const calculateExitValue = (
  exitPrice: number,
  exitQuantity: number,
  entryQuantity: number
): number => {
  if (exitPrice <= 0) {
    return 0;
  }
  const quantity = exitQuantity || entryQuantity;
  if (quantity <= 0) {
    return 0;
  }
  return exitPrice * quantity;
};

/**
 * Calculate gross profit/loss
 * For long positions (buy): exitValue - entryValue
 * For short positions (sell): entryValue - exitValue
 */
export const calculateGrossPnL = (
  entryValue: number,
  exitValue: number,
  tradeType: "buy" | "sell" = "buy"
): number => {
  if (tradeType === "sell") {
    // Short position: profit when price goes down
    return entryValue - exitValue;
  } else {
    // Long position: profit when price goes up
  return exitValue - entryValue;
  }
};

/**
 * Calculate total fees (entryFees + exitFees)
 */
export const calculateTotalFees = (
  entryFees: number,
  exitFees: number = 0
): number => {
  return (entryFees || 0) + (exitFees || 0);
};

/**
 * Calculate net profit/loss (grossPnL - totalFees)
 */
export const calculateNetPnL = (
  grossPnL: number,
  totalFees: number
): number => {
  return grossPnL - totalFees;
};

/**
 * Calculate return percentage ((netPnL / entryValue) * 100)
 */
export const calculateReturnPercent = (
  netPnL: number,
  entryValue: number
): number => {
  if (entryValue === 0) {
    return 0;
  }
  return (netPnL / entryValue) * 100;
};

/**
 * Calculate ROI (return on investment)
 * Same as return percentage for now, but can be extended
 */
export const calculateROI = (
  netPnL: number,
  entryValue: number
): number => {
  return calculateReturnPercent(netPnL, entryValue);
};

/**
 * Calculate all trade values from input data
 * Returns an object with all calculated fields
 */
export const calculateTradeValues = (trade: {
  entryPrice: number;
  entryQuantity: number;
  entryFees?: number;
  exitPrice?: number;
  exitQuantity?: number;
  exitFees?: number;
  status?: "open" | "closed";
  type?: "buy" | "sell";
}): {
  entryValue: number;
  exitValue?: number;
  grossPnL?: number;
  netPnL?: number;
  returnPercent?: number;
  roi?: number;
} => {
  const entryValue = calculateEntryValue(trade.entryPrice, trade.entryQuantity);
  
  if (!trade.exitPrice || trade.status === "open") {
    return {
      entryValue,
    };
  }
  
  const exitValue = calculateExitValue(
    trade.exitPrice,
    trade.exitQuantity || trade.entryQuantity,
    trade.entryQuantity
  );
  
  const grossPnL = calculateGrossPnL(entryValue, exitValue, trade.type || "buy");
  const totalFees = calculateTotalFees(trade.entryFees || 0, trade.exitFees || 0);
  const netPnL = calculateNetPnL(grossPnL, totalFees);
  const returnPercent = calculateReturnPercent(netPnL, entryValue);
  const roi = calculateROI(netPnL, entryValue);
  
  return {
    entryValue,
    exitValue,
    grossPnL,
    netPnL,
    returnPercent,
    roi,
  };
};

/**
 * Calculate statistics from an array of trades
 */
export const calculateTradeStatistics = (trades: Trade[]): TradeStatistics => {
  const closedTrades = trades.filter((t) => t.status === "closed");
  const openTrades = trades.filter((t) => t.status === "open");
  
  const winningTrades = closedTrades.filter((t) => (t.netPnL || 0) > 0);
  const losingTrades = closedTrades.filter((t) => (t.netPnL || 0) < 0);
  
  const totalPnL = closedTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0);
  
  const winRate =
    closedTrades.length > 0
      ? (winningTrades.length / closedTrades.length) * 100
      : 0;
  
  const wins = winningTrades.map((t) => t.netPnL || 0);
  const losses = losingTrades.map((t) => t.netPnL || 0);
  
  const averageWin = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
  const averageLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
  
  const largestWin = wins.length > 0 ? Math.max(...wins) : 0;
  const largestLoss = losses.length > 0 ? Math.min(...losses) : 0;
  
  const totalReturnPercent =
    closedTrades.length > 0
      ? closedTrades.reduce((sum, t) => sum + (t.returnPercent || 0), 0) / closedTrades.length
      : 0;
  
  const averageReturnPercent = totalReturnPercent;
  
  // Calculate most traded symbol
  const symbolCounts: { [key: string]: number } = {};
  trades.forEach((trade) => {
    if (trade.symbol) {
      symbolCounts[trade.symbol] = (symbolCounts[trade.symbol] || 0) + 1;
    }
  });
  
  let mostTradedSymbol: string | undefined;
  let mostTradedSymbolCount: number | undefined;
  
  if (Object.keys(symbolCounts).length > 0) {
    const sortedSymbols = Object.entries(symbolCounts).sort((a, b) => b[1] - a[1]);
    mostTradedSymbol = sortedSymbols[0][0];
    mostTradedSymbolCount = sortedSymbols[0][1];
  }
  
  return {
    totalTrades: trades.length,
    openTrades: openTrades.length,
    closedTrades: closedTrades.length,
    winningTrades: winningTrades.length,
    losingTrades: losingTrades.length,
    winRate: Math.round(winRate * 100) / 100, // Round to 2 decimal places
    totalPnL: Math.round(totalPnL * 100) / 100,
    averageWin: Math.round(averageWin * 100) / 100,
    averageLoss: Math.round(averageLoss * 100) / 100,
    largestWin: Math.round(largestWin * 100) / 100,
    largestLoss: Math.round(largestLoss * 100) / 100,
    totalReturnPercent: Math.round(totalReturnPercent * 100) / 100,
    averageReturnPercent: Math.round(averageReturnPercent * 100) / 100,
    mostTradedSymbol,
    mostTradedSymbolCount,
  };
};

/**
 * Calculate win streak and loss streak
 */
export const calculateStreaks = (trades: Trade[]): {
  currentWinStreak: number;
  currentLossStreak: number;
  longestWinStreak: number;
  longestLossStreak: number;
} => {
  const closedTrades = trades
    .filter((t) => t.status === "closed")
    .sort((a, b) => {
      const dateA = new Date(a.exitDate || a.entryDate).getTime();
      const dateB = new Date(b.exitDate || b.entryDate).getTime();
      return dateB - dateA; // Most recent first
    });

  let currentWinStreak = 0;
  let currentLossStreak = 0;
  let longestWinStreak = 0;
  let longestLossStreak = 0;
  let tempWinStreak = 0;
  let tempLossStreak = 0;

  closedTrades.forEach((trade) => {
    const pnl = trade.netPnL || 0;
    if (pnl > 0) {
      tempWinStreak++;
      tempLossStreak = 0;
      if (tempWinStreak > longestWinStreak) {
        longestWinStreak = tempWinStreak;
      }
      if (currentWinStreak === 0 && currentLossStreak === 0) {
        currentWinStreak = tempWinStreak;
      } else if (currentWinStreak > 0) {
        currentWinStreak = tempWinStreak;
      }
    } else if (pnl < 0) {
      tempLossStreak++;
      tempWinStreak = 0;
      if (tempLossStreak > longestLossStreak) {
        longestLossStreak = tempLossStreak;
      }
      if (currentWinStreak === 0 && currentLossStreak === 0) {
        currentLossStreak = tempLossStreak;
      } else if (currentLossStreak > 0) {
        currentLossStreak = tempLossStreak;
      }
    }
  });

  return {
    currentWinStreak,
    currentLossStreak,
    longestWinStreak,
    longestLossStreak,
  };
};

/**
 * Calculate maximum drawdown
 */
export const calculateMaxDrawdown = (trades: Trade[]): number => {
  const closedTrades = trades
    .filter((t) => t.status === "closed")
    .sort((a, b) => {
      const dateA = new Date(a.exitDate || a.entryDate).getTime();
      const dateB = new Date(b.exitDate || b.entryDate).getTime();
      return dateA - dateB; // Oldest first
    });

  if (closedTrades.length === 0) return 0;

  let peak = 0;
  let maxDrawdown = 0;
  let cumulativePnL = 0;

  closedTrades.forEach((trade) => {
    cumulativePnL += trade.netPnL || 0;
    if (cumulativePnL > peak) {
      peak = cumulativePnL;
    }
    const drawdown = peak - cumulativePnL;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
    }
  });

  return Math.round(maxDrawdown * 100) / 100;
};

/**
 * Calculate Sharpe ratio (simplified version)
 * Sharpe Ratio = (Average Return - Risk Free Rate) / Standard Deviation of Returns
 * For simplicity, we'll use 0 as risk-free rate
 */
export const calculateSharpeRatio = (trades: Trade[]): number => {
  const closedTrades = trades.filter((t) => t.status === "closed" && t.returnPercent !== undefined);
  
  if (closedTrades.length < 2) return 0;

  const returns = closedTrades.map((t) => t.returnPercent || 0);
  const averageReturn = returns.reduce((a, b) => a + b, 0) / returns.length;

  // Calculate standard deviation
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - averageReturn, 2), 0) / returns.length;
  const stdDev = Math.sqrt(variance);

  if (stdDev === 0) return 0;

  // Simplified Sharpe ratio (assuming risk-free rate = 0)
  const sharpeRatio = averageReturn / stdDev;
  return Math.round(sharpeRatio * 100) / 100;
};

/**
 * Calculate average trade duration in days
 */
export const calculateAverageTradeDuration = (trades: Trade[]): number => {
  const closedTrades = trades.filter(
    (t) => t.status === "closed" && t.entryDate && t.exitDate
  );

  if (closedTrades.length === 0) return 0;

  const totalDays = closedTrades.reduce((sum, trade) => {
    const entryDate = new Date(trade.entryDate);
    const exitDate = new Date(trade.exitDate!);
    const diffTime = exitDate.getTime() - entryDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return sum + diffDays;
  }, 0);

  return Math.round((totalDays / closedTrades.length) * 100) / 100;
};

/**
 * Calculate symbol statistics
 */
export interface SymbolStats {
  symbol: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  winRate: number;
  totalPnL: number;
  averagePnL: number;
  bestTrade: number;
  worstTrade: number;
}

export const calculateSymbolStatistics = (trades: Trade[]): SymbolStats[] => {
  const symbolMap: { [key: string]: Trade[] } = {};

  trades.forEach((trade) => {
    if (!symbolMap[trade.symbol]) {
      symbolMap[trade.symbol] = [];
    }
    symbolMap[trade.symbol].push(trade);
  });

  return Object.entries(symbolMap).map(([symbol, symbolTrades]) => {
    const closedTrades = symbolTrades.filter((t) => t.status === "closed");
    const winningTrades = closedTrades.filter((t) => (t.netPnL || 0) > 0);
    const losingTrades = closedTrades.filter((t) => (t.netPnL || 0) < 0);
    const totalPnL = closedTrades.reduce((sum, t) => sum + (t.netPnL || 0), 0);
    const winRate = closedTrades.length > 0 ? (winningTrades.length / closedTrades.length) * 100 : 0;
    const averagePnL = closedTrades.length > 0 ? totalPnL / closedTrades.length : 0;
    const pnls = closedTrades.map((t) => t.netPnL || 0);
    const bestTrade = pnls.length > 0 ? Math.max(...pnls) : 0;
    const worstTrade = pnls.length > 0 ? Math.min(...pnls) : 0;

    return {
      symbol,
      totalTrades: symbolTrades.length,
      winningTrades: winningTrades.length,
      losingTrades: losingTrades.length,
      winRate: Math.round(winRate * 100) / 100,
      totalPnL: Math.round(totalPnL * 100) / 100,
      averagePnL: Math.round(averagePnL * 100) / 100,
      bestTrade: Math.round(bestTrade * 100) / 100,
      worstTrade: Math.round(worstTrade * 100) / 100,
    };
  });
};

/**
 * Get best and worst performing symbols
 */
export const getBestWorstSymbols = (trades: Trade[]): {
  best?: SymbolStats;
  worst?: SymbolStats;
} => {
  const symbolStats = calculateSymbolStatistics(trades);
  const symbolsWithTrades = symbolStats.filter((s) => s.totalTrades > 0);

  if (symbolsWithTrades.length === 0) {
    return {};
  }

  const best = symbolsWithTrades.reduce((prev, current) =>
    current.totalPnL > prev.totalPnL ? current : prev
  );
  const worst = symbolsWithTrades.reduce((prev, current) =>
    current.totalPnL < prev.totalPnL ? current : prev
  );

  return { best, worst };
};

/**
 * Calculate Risk/Reward Ratio
 * Risk/Reward Ratio = Average Win / Average Loss
 * This indicates how much profit is made on average for every dollar lost
 * 
 * @param trades Array of trades
 * @returns Risk/reward ratio (or null if insufficient data)
 */
export const calculateRiskRewardRatio = (trades: Trade[]): number | null => {
  const closedTrades = trades.filter((t) => t.status === "closed" && t.netPnL !== undefined);
  
  if (closedTrades.length === 0) {
    return null;
  }

  const wins = closedTrades
    .filter((t) => (t.netPnL || 0) > 0)
    .map((t) => t.netPnL || 0);
  
  const losses = closedTrades
    .filter((t) => (t.netPnL || 0) < 0)
    .map((t) => Math.abs(t.netPnL || 0)); // Use absolute value for losses

  const averageWin = wins.length > 0 ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
  const averageLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;

  // If there are no losses, we can't calculate a meaningful ratio
  if (averageLoss === 0) {
    return null;
  }

  // If there are no wins, the ratio is 0
  if (averageWin === 0) {
    return 0;
  }

  // Calculate ratio: average win / average loss
  return averageWin / averageLoss;
};

/**
 * Trades with a realized close suitable for P&L / intraday series (excludes open positions).
 */
export const isRealizedClosedTrade = (t: Trade): boolean => {
  if (String(t.status ?? "").toLowerCase() !== "closed") return false;
  if (t.exitDate == null || String(t.exitDate).trim() === "") return false;
  if (typeof t.netPnL !== "number" || !Number.isFinite(t.netPnL)) return false;
  return true;
};

